// ============================================================
// ClasswiseEnrollmentOvertime — replaces app-classwise-enrollment-overtime
// Loads CSV 10187.csv, aggregates by class and academic year
// ============================================================
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Papa from 'papaparse';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  parseCsvToObjects,
  sumField,
  getMngTypeOnUserType,
  calculateFilteredData,
} from '../../shared/utils';
import { RootState } from '../../../../store';
import ChartPanelWrapper from '../../../../shared/graph/graphPanelWrapper';
import SharedFilter from '../../../../shared/filter/sharedFilter';
import { FilterState } from '../../../../shared/filter/filterTypes';
import '../../../../shared/filter/sharedFilter.scss';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import '../../dashboard.scss';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const CSV_URL = 'https://d3ijhv7dn0xr3b.cloudfront.net/10187.csv';
const YEARS = ['2020-21', '2021-22', '2022-23'];
const COLORS = ['#05a34a', '#fbbc06', '#5970ca'];

const CLASS_LIST = [
  { id: '1', label: 'PRE KG' },
  { id: '2', label: 'LKG' },
  { id: '3', label: 'UKG' },
  { id: '4', label: 'Class I' },
  { id: '5', label: 'Class II' },
  { id: '6', label: 'Class III' },
  { id: '7', label: 'Class IV' },
  { id: '8', label: 'Class V' },
  { id: '9', label: 'Class VI' },
  { id: '10', label: 'Class VII' },
  { id: '11', label: 'Class VIII' },
  { id: '12', label: 'Class IX' },
  { id: '13', label: 'Class X' },
  { id: '14', label: 'Class XI' },
  { id: '15', label: 'Class XII' },
];

interface ClassRow {
  Class: string;
  year1: number;
  year2: number;
  year3: number;
}

const ClasswiseEnrollmentOvertime: React.FC = () => {
  const s = useSelector((state: RootState) => state.session);
  const usertypeid = s.usertypeid ?? '';
  const emisUsertype1 = s.emisUsertype1 ?? '';
  const districtId = String(s.districtId ?? '');
  const edu_district_id = String(s.edu_district_id ?? '');
  const blockId = String(s.blockId ?? '');

  const [overallData, setOverallData] = useState<any[]>([]);
  const [finalList, setFinalList] = useState<ClassRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [globalFilter, setGlobalFilter] = useState<string>('');
  const [filterState, setFilterState] = useState<FilterState | null>(null);
  console.log('globalFilter', globalFilter);
  console.log('filterState', filterState);
  useEffect(() => {
    Papa.parse(CSV_URL, {
      download: true,
      complete: (results) => {
        let rows = parseCsvToObjects(results.data as string[][]);
        if (usertypeid === '24' && ['5', '6'].includes(emisUsertype1)) {
          rows = rows.filter((x) => String(x.manage_id) === '2');
        }
        setOverallData(rows);
        setLoading(false);
      },
      error: () => setLoading(false),
    });
  }, [usertypeid, emisUsertype1]);

  useEffect(() => {
    if (overallData.length === 0) return;

    // Apply shared filter
    let data = overallData;
    if (filterState) {
      data = calculateFilteredData(
        overallData,
        filterState as unknown as Record<string, boolean>
      );
    }

    const geoType = getMngTypeOnUserType(usertypeid, emisUsertype1);
    let filtered = data;

    if (typeof geoType === 'number' || Array.isArray(geoType)) {
      const ids = ([] as number[]).concat(geoType as any).map(String);
      filtered = data.filter((x) => ids.includes(String(x.manage_id)));
    } else if (geoType === 'district') {
      filtered = data.filter((x) => String(x.district_id) === districtId);
    } else if (geoType === 'edu_district') {
      filtered = data.filter((x) => String(x.edu_dist_id) === edu_district_id);
    } else if (geoType === 'block') {
      filtered = data.filter((x) => String(x.block_id) === blockId);
    }

    // Determine which classes to show based on filter state
    const activeClassIds: string[] = [];
    if (
      !filterState ||
      (filterState.pre_kg &&
        filterState.lkg &&
        filterState.ukg &&
        filterState.class1 &&
        filterState.class2 &&
        filterState.class3 &&
        filterState.class4 &&
        filterState.class5 &&
        filterState.class6 &&
        filterState.class7 &&
        filterState.class8 &&
        filterState.class9 &&
        filterState.class10 &&
        filterState.class11 &&
        filterState.class12)
    ) {
      // All checked — show all classes
      CLASS_LIST.forEach((cls) => activeClassIds.push(cls.id));
    } else {
      if (filterState.pre_kg) activeClassIds.push('1');
      if (filterState.lkg) activeClassIds.push('2');
      if (filterState.ukg) activeClassIds.push('3');
      if (filterState.class1) activeClassIds.push('4');
      if (filterState.class2) activeClassIds.push('5');
      if (filterState.class3) activeClassIds.push('6');
      if (filterState.class4) activeClassIds.push('7');
      if (filterState.class5) activeClassIds.push('8');
      if (filterState.class6) activeClassIds.push('9');
      if (filterState.class7) activeClassIds.push('10');
      if (filterState.class8) activeClassIds.push('11');
      if (filterState.class9) activeClassIds.push('12');
      if (filterState.class10) activeClassIds.push('13');
      if (filterState.class11) activeClassIds.push('14');
      if (filterState.class12) activeClassIds.push('15');
    }

    const visibleClasses = CLASS_LIST.filter((cls) =>
      activeClassIds.includes(cls.id)
    );

    const rows: ClassRow[] = visibleClasses.map((cls) => {
      const classData = filtered.filter(
        (x) => String(x.class_studying_id) === cls.id
      );
      return {
        Class: cls.label,
        year1: sumField(
          classData.filter((x) => x.Acac_yr === YEARS[0]),
          'count(scd.id)'
        ),
        year2: sumField(
          classData.filter((x) => x.Acac_yr === YEARS[1]),
          'count(scd.id)'
        ),
        year3: sumField(
          classData.filter((x) => x.Acac_yr === YEARS[2]),
          'count(scd.id)'
        ),
      };
    });
    queueMicrotask(() => setFinalList(rows));
  }, [
    overallData,
    filterState,
    usertypeid,
    emisUsertype1,
    districtId,
    edu_district_id,
    blockId,
  ]);

  const handleFilterChange = useCallback((filters: FilterState) => {
    setFilterState(filters);
  }, []);

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" />
      </div>
    );
  }

  const chartData = {
    labels: finalList.map((r) => r.Class),
    datasets: [
      {
        label: YEARS[0],
        data: finalList.map((r) => r.year1),
        backgroundColor: COLORS[0],
      },
      {
        label: YEARS[1],
        data: finalList.map((r) => r.year2),
        backgroundColor: COLORS[1],
      },
      {
        label: YEARS[2],
        data: finalList.map((r) => r.year3),
        backgroundColor: COLORS[2],
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: { legend: { position: 'top' as const } },
    scales: { y: { title: { display: true, text: 'Enrollment Number' } } },
  };

  const chartContent = (
    <>
      <div className="d-flex justify-content-center align-items-center">
        <p className="text-muted small">(Hover on the Graph to view Numbers)</p>
      </div>
      <Bar data={chartData} options={options} />
    </>
  );

  const gridContent = (
    <div className="comparative-grid mt-3">
      <div className="mb-2">
        <input
          type="text"
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder="Search..."
          className="global-search-input"
        />
      </div>
      <DataTable
        value={finalList}
        sortMode="single"
        size="small"
        stripedRows
        emptyMessage="No data found"
        globalFilter={globalFilter}
        globalFilterFields={['Class', 'year1', 'year2', 'year3']}
      >
        <Column field="Class" header="Class" sortable />
        <Column
          field="year1"
          header="2020-21"
          sortable
          body={(row) => row.year1.toLocaleString('en-IN')}
        />
        <Column
          field="year2"
          header="2021-22"
          sortable
          body={(row) => row.year2.toLocaleString('en-IN')}
        />
        <Column
          field="year3"
          header="2022-23"
          sortable
          body={(row) => row.year3.toLocaleString('en-IN')}
        />
      </DataTable>
    </div>
  );

  return (
    <div className="chart-with-filter">
      <SharedFilter showFilters={['6']} onFilterChange={handleFilterChange} />
      <ChartPanelWrapper
        title=""
        chartContent={chartContent}
        gridContent={gridContent}
        loading={loading}
      />
    </div>
  );
};

export default ClasswiseEnrollmentOvertime;
